import React from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { StatusPill, AnalyzingBadge } from "@/components/StatusPill";
import { useOrdersPolling } from "@/hooks/useOrdersPolling";
import { FileStack, ChevronRight, Clock } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";
import { useI18n } from "@/i18n";

export const ClientOrders = () => {
  const { orders, loading } = useOrdersPolling();
  const { t, lang } = useI18n();

  return (
    <AppShell mode="client">
      <div className="mb-8">
        <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-2">{t("orders.kicker")}</div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">{t("orders.title")}</h1>
      </div>

      <div className="glass rounded-md overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          <div className="col-span-3">{t("orders.colVehicle")}</div>
          <div className="col-span-3">{t("orders.colFile")}</div>
          <div className="col-span-3">{t("orders.colSolutions")}</div>
          <div className="col-span-1">{t("orders.colCredits")}</div>
          <div className="col-span-2 text-right">{t("orders.colStatus")}</div>
        </div>
        {loading ? (
          <div className="font-mono text-xs text-zinc-500 py-12 text-center">{t("orders.loading")}</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <FileStack className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm mb-4">{t("orders.none")}</p>
            <Link to="/client/new" className="px-5 py-2.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-sm rounded-sm">{t("orders.depositFile")}</Link>
          </div>
        ) : (
          orders.map((o) => (
            <Link key={o.id} to={`/client/orders/${o.id}`} data-testid={`order-row-${o.id}`} className="grid grid-cols-2 md:grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors items-center">
              <div className="md:col-span-3">
                <div className="font-display uppercase tracking-tight">{o.vehicle_brand} {o.vehicle_model}</div>
                <div className="font-mono text-[10px] text-zinc-500">{fmtDateTime(o.created_at, lang)}</div>
              </div>
              <div className="md:col-span-3 font-mono text-xs text-zinc-400 truncate hidden md:block">{o.original_filename}</div>
              <div className="md:col-span-3 font-mono text-[10px] text-zinc-500 hidden md:block">{o.services.join(", ")}</div>
              <div className="md:col-span-1 font-mono text-sm text-aurora-cyan hidden md:block">{o.total_credits}</div>
              <div className="md:col-span-2 flex justify-end items-center gap-2">
                {o.analyzing ? <AnalyzingBadge testid={`analyzing-${o.id}`} /> : <StatusPill status={o.status} testid={`status-${o.id}`} />}
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </div>
            </Link>
          ))
        )}
      </div>
    </AppShell>
  );
};
